import { GRID_SIZE, NODE_DIMENSIONS } from './constants';

/**
 * Bounding box used by the naive collision solver (xyflow node-collision-algorithms).
 * @typedef {{ x: number, y: number, width: number, height: number, moved: boolean, node: import('reactflow').Node }} CollisionBox
 */

/**
 * Fallback sizes when React Flow has not yet measured width/height.
 * @param {import('reactflow').Node} node
 */
function getCollisionBounds(node) {
  const w = node.width;
  const h = node.height;
  if (w != null && w > 0 && h != null && h > 0) {
    return { width: w, height: h };
  }

  if (node.type === 'text') {
    const tw = node.data?.width || 300;
    return { width: tw, height: Math.max(72, node.data?.height || 100) };
  }

  const fields = node.data?.fields?.length ?? 0;
  const collapsed = !!node.data?.collapsed;
  const header = 52;
  const row = 26;
  const extra = collapsed ? 40 : Math.max(1, fields) * row + 32;
  return {
    width: NODE_DIMENSIONS.width + 16,
    height: Math.max(NODE_DIMENSIONS.minHeight, header + extra),
  };
}

function snapCoord(value, grid) {
  return Math.round(value / grid) * grid;
}

/**
 * Naive O(n²) collision resolution — same approach as
 * https://reactflow.dev/examples/layout/node-collisions
 *
 * @param {import('reactflow').Node[]} nodes
 * @param {object} [options]
 * @param {number} [options.maxIterations] Use 200 when Infinity (docs example)
 * @param {number} [options.overlapThreshold]
 * @param {number} [options.margin] Extra space between node rects
 * @param {number} [options.snapGrid] Align final positions to canvas grid (default GRID_SIZE)
 * @returns {import('reactflow').Node[]}
 */
export function resolveCollisions(nodes, options = {}) {
  const {
    maxIterations = 200,
    overlapThreshold = 0.5,
    margin = 15,
    snapGrid = GRID_SIZE,
  } = options;

  const cap =
    maxIterations === Infinity || maxIterations > 500 ? 500 : maxIterations;

  const visible = nodes.filter((n) => !n.hidden);
  if (visible.length < 2) {
    return nodes;
  }

  /** @type {CollisionBox[]} */
  const boxes = visible.map((node) => {
    const { width, height } = getCollisionBounds(node);
    return {
      x: node.position.x - margin,
      y: node.position.y - margin,
      width: width + margin * 2,
      height: height + margin * 2,
      node,
      moved: false,
    };
  });

  for (let iter = 0; iter <= cap; iter++) {
    let moved = false;

    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const A = boxes[i];
        const B = boxes[j];

        const centerAX = A.x + A.width * 0.5;
        const centerAY = A.y + A.height * 0.5;
        const centerBX = B.x + B.width * 0.5;
        const centerBY = B.y + B.height * 0.5;

        const dx = centerAX - centerBX;
        const dy = centerAY - centerBY;

        const px = (A.width + B.width) * 0.5 - Math.abs(dx);
        const py = (A.height + B.height) * 0.5 - Math.abs(dy);

        if (px > overlapThreshold && py > overlapThreshold) {
          A.moved = B.moved = moved = true;
          if (px < py) {
            const sx = dx > 0 ? 1 : -1;
            const moveAmount = (px / 2) * sx;
            A.x += moveAmount;
            B.x -= moveAmount;
          } else {
            const sy = dy > 0 ? 1 : -1;
            const moveAmount = (py / 2) * sy;
            A.y += moveAmount;
            B.y -= moveAmount;
          }
        }
      }
    }

    if (!moved) break;
  }

  if (!boxes.some((b) => b.moved)) {
    return nodes;
  }

  const resolvedVisible = boxes.map((box) => {
    if (!box.moved) return box.node;
    const x = snapCoord(box.x + margin, snapGrid);
    const y = snapCoord(box.y + margin, snapGrid);
    return {
      ...box.node,
      position: { x, y },
    };
  });

  const byId = new Map(resolvedVisible.map((n) => [n.id, n]));
  return nodes.map((n) => byId.get(n.id) ?? n);
}
